import CountrySelector from '../CountrySelector';
import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, ShoppingCart, Heart, User, Menu } from 'lucide-react';
import { Button } from '../ui/button';
import AuthModal from '../modals/AuthModal';
import SearchModal from '../modals/SearchModal';
import CartSidebar from './CartSidebar';
import WishlistSidebar from './WishlistSidebar';
import AccountSidebar from './AccountSidebar';
import MobileMenu from './MobileMenu';
import DesktopCategoryNav from './DesktopCategoryNav';
import MobileCategoryNav from './MobileCategoryNav';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import { useSlider } from '../../contexts/SliderContext';
import { useCurrentUser } from '../../hooks/useCurrentUser';

const Header = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { cartCount, isCartOpen, setIsCartOpen } = useCart();
  const { wishlistCount } = useWishlist();
  const { currentSliderTextColor } = useSlider();
  const { isAdmin, isAuthenticated } = useCurrentUser();
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isWishlistOpen, setIsWishlistOpen] = useState(false);
  const [isAccountSidebarOpen, setIsAccountSidebarOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHidden, setIsHidden] = useState(false);

  const isHomePage = location.pathname === '/';

  // ProtectedRoute sends logged-out visitors to /?login=1 → open the login pop-up.
  React.useEffect(() => {
    if (new URLSearchParams(location.search).get('login') !== '1') return;
    if (!localStorage.getItem('accessToken')) setIsAuthModalOpen(true);
    navigate(location.pathname, { replace: true });
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if user is admin (uses /api/auth/me via useCurrentUser hook —
  // do NOT decode the JWT here; the backend token doesn't carry `role`).
  const handleAccountClick = () => {
    if (!isAuthenticated) {
      // Not logged in - show login modal
      setIsAuthModalOpen(true);
    } else if (isAdmin) {
      // Admin user - navigate to admin panel
      navigate('/admin');
    } else {
      // Regular user - show account sidebar
      setIsAccountSidebarOpen(true);
    }
  };

  // Sticky header that hides on scroll-down and reappears on scroll-up.
  // Always visible near the top (first 80px) so the hero never gets covered.
  React.useEffect(() => {
    let lastY = window.scrollY;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        const y = window.scrollY;
        setIsScrolled(y > 20);
        const delta = y - lastY;
        if (y < 80) {
          setIsHidden(false);
        } else if (delta > 6) {
          // scrolling down
          setIsHidden(true);
        } else if (delta < -6) {
          // scrolling up
          setIsHidden(false);
        }
        lastY = y;
        raf = 0;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Determine if header should use light text (white) based on current slider
  // With the new soft cream BG always behind the header, icons stay dark.
  // (Previously: white icons over hero photo. Now we have a cream backdrop blur, so ink reads cleanly.)
  const useLightText = false;

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 z-50 w-full transition-transform duration-300 ease-out"
        style={{
          background: isHomePage && !isScrolled
            ? 'rgba(247, 244, 239, 0.55)'
            : 'rgba(247, 244, 239, 0.95)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          borderBottom: isHomePage && !isScrolled
            ? '1px solid rgba(216, 210, 197, 0.4)'
            : '1px solid var(--line)',
          transform: isHidden ? 'translateY(-100%)' : 'translateY(0)',
        }}
        data-testid="site-header"
      >
        {/* Main Header */}
        <div className={`container mx-auto px-4 ${isHomePage ? 'py-2 md:py-4' : 'py-4'}`}>
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img 
                src="/logo-cimonamia.png" 
                alt="Cimonamia Logo" 
                className="h-8 md:h-10 w-auto transition-all"
                style={{ 
                  filter: useLightText ? 'brightness(0) invert(1)' : 'none'
                }}
              />
            </Link>

            {/* Desktop Navigation with Categories */}
            <nav className="hidden lg:flex items-center gap-8">
              <DesktopCategoryNav useLightText={useLightText} />
            </nav>

            {/* Icons */}
            <div className="flex items-center gap-2 md:gap-4">
              {/* Country / currency (desktop) */}
              <div className="hidden md:block">
                <CountrySelector variant="header" light={useLightText} />
              </div>

              {/* Search */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsSearchModalOpen(true)}
                aria-label={t('nav.search')}
              >
                <Search
                  className={`h-5 w-5 transition-colors ${useLightText ? 'text-white drop-shadow-lg' : ''}`}
                  strokeWidth={1.5}
                  style={!useLightText ? { color: 'var(--ink)' } : undefined}
                />
              </Button>

              {/* Account */}
              <Button
                variant="ghost"
                size="icon"
                onClick={handleAccountClick}
                aria-label={t('nav.account')}
              >
                <User
                  className={`h-5 w-5 transition-colors ${useLightText ? 'text-white drop-shadow-lg' : ''}`}
                  strokeWidth={1.5}
                  style={!useLightText ? { color: 'var(--ink)' } : undefined}
                />
              </Button>

              {/* Wishlist */}
              <Button
                variant="ghost"
                size="icon"
                className="relative"
                onClick={() => setIsWishlistOpen(true)}
                aria-label={t('nav.wishlist')}
              >
                <Heart
                  className={`h-5 w-5 transition-colors ${useLightText ? 'text-white drop-shadow-lg' : ''}`}
                  strokeWidth={1.5}
                  style={!useLightText ? { color: 'var(--ink)' } : undefined}
                />
                {wishlistCount > 0 && (
                  <span
                    className="absolute -top-1 -right-1 text-white rounded-full h-5 w-5 flex items-center justify-center"
                    style={{ background: 'var(--terracotta)', fontSize: '10px' }}
                  >
                    {wishlistCount}
                  </span>
                )}
              </Button>

              {/* Cart */}
              <Button
                variant="ghost"
                size="icon"
                className="relative"
                onClick={() => setIsCartOpen(true)}
                aria-label={t('nav.cart')}
              >
                <ShoppingCart
                  className={`h-5 w-5 transition-colors ${useLightText ? 'text-white drop-shadow-lg' : ''}`}
                  strokeWidth={1.5}
                  style={!useLightText ? { color: 'var(--ink)' } : undefined}
                />
                {cartCount > 0 && (
                  <span
                    className="absolute -top-1 -right-1 text-white rounded-full h-5 w-5 flex items-center justify-center"
                    style={{ background: 'var(--terracotta)', fontSize: '10px' }}
                  >
                    {cartCount}
                  </span>
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Spacer for non-home pages so the fixed header never covers content.
          Home page intentionally lets the hero sit under the translucent bar. */}
      {!isHomePage && <div aria-hidden="true" style={{ height: '72px' }} />}

      {/* Mobile Category Navigation */}
      <MobileCategoryNav />

      {/* Modals & Sidebars */}
      <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />
      <SearchModal isOpen={isSearchModalOpen} onClose={() => setIsSearchModalOpen(false)} />
      <CartSidebar isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
      <WishlistSidebar isOpen={isWishlistOpen} onClose={() => setIsWishlistOpen(false)} />
      <AccountSidebar isOpen={isAccountSidebarOpen} onClose={() => setIsAccountSidebarOpen(false)} />
      <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
    </>
  );
};

export default Header;